import { AnimatePresence, motion } from 'framer-motion'
import { CalendarClock, CheckCircle2, ClipboardCheck, TriangleAlert, UserPlus } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppData } from '../context/AppDataContext'
import { useUI } from '../context/UIContext'
import { cn } from '../lib/cn'
import type { Notification } from '../data/types'

const iconMap: Record<Notification['type'], typeof CheckCircle2> = {
  task: ClipboardCheck,
  deliverable: CheckCircle2,
  approval: CheckCircle2,
  overdue: TriangleAlert,
  team: UserPlus,
}

const colorMap: Record<Notification['type'], string> = {
  task: 'text-info-500 bg-info-50',
  deliverable: 'text-success-500 bg-success-50',
  approval: 'text-brand-500 bg-brand-50',
  overdue: 'text-danger-500 bg-danger-50',
  team: 'text-warning-500 bg-warning-50',
}

/**
 * Anchoring `right-0` to the bell button pushed a 340px panel off the left
 * edge of a phone screen. Below `sm` the panel spans the viewport with a
 * small inset instead; from `sm` up it goes back to hanging off the button.
 */
const PANEL_CLASS =
  'fixed inset-x-3 top-[4.25rem] z-50 overflow-hidden rounded-2xl border border-shell-border bg-shell shadow-pop sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-96'

export function NotificationsPanel() {
  const { notificationsOpen, setNotificationsOpen } = useUI()
  const { notifications, markNotificationRead, markAllNotificationsRead } = useAppData()
  const ref = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  function openNotification(n: Notification) {
    markNotificationRead(n.id)
    setNotificationsOpen(false)
    if (n.href) navigate(n.href)
  }

  useEffect(() => {
    if (!notificationsOpen) return
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setNotificationsOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [notificationsOpen, setNotificationsOpen])

  return (
    <AnimatePresence>
      {notificationsOpen && (
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: -8, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -6, scale: 0.97, transition: { duration: 0.12 } }}
          transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
          className={PANEL_CLASS}
        >
          <div className="flex items-center justify-between border-b border-shell-border px-4 py-3">
            <h3 className="font-display text-sm font-semibold text-shell-text-active">Notifications</h3>
            <button onClick={markAllNotificationsRead} className="text-xs font-medium text-brand-500 hover:text-brand-600">
              Mark all read
            </button>
          </div>
          <div className="max-h-96 overflow-y-auto scrollbar-thin">
            {notifications.map((n) => {
              const Icon = iconMap[n.type]
              return (
                <button
                  key={n.id}
                  onClick={() => openNotification(n)}
                  className={cn(
                    'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-shell-border/40',
                    !n.read && 'bg-brand-50/40',
                  )}
                >
                  <div className={cn('mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full', colorMap[n.type])}>
                    <Icon size={14} />
                  </div>
                  <div className="flex-1">
                    <p className={cn('text-sm leading-snug', n.read ? 'text-shell-text' : 'text-shell-text-active font-medium')}>{n.message}</p>
                    <p className="mt-0.5 flex items-center gap-1 text-xs text-shell-text">
                      <CalendarClock size={11} />
                      {n.time}
                    </p>
                  </div>
                  {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-brand-500" />}
                </button>
              )
            })}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
